import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IdeaCategory, IdeaStatus, IdeaVisibility } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const ideasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ideas',
  component: IdeasListPage,
});

const STATUS_VARIANT: Record<IdeaStatus, 'default' | 'muted' | 'destructive'> = {
  draft: 'muted',
  proposed: 'muted',
  under_review: 'default',
  approved: 'default',
  rejected: 'destructive',
  parked: 'muted',
};

function IdeasListPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = React.useState<IdeaStatus | ''>('');
  const [open, setOpen] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [pitch, setPitch] = React.useState('');
  const [category, setCategory] = React.useState<IdeaCategory>('process');
  const [visibility, setVisibility] = React.useState<IdeaVisibility>('c_suite_and_sponsor');
  const [error, setError] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['ideas', statusFilter],
    queryFn: () =>
      statusFilter === '' ? api.listIdeas() : api.listIdeas({ status: statusFilter as IdeaStatus }),
  });

  const reviewsQuery = useQuery({
    queryKey: ['initiatives-reviews-due'],
    queryFn: () => api.listInitiativesDueForReview(),
  });

  const createMutation = useMutation({
    mutationFn: () => api.createIdea({ title, pitch, category, visibility }),
    onSuccess: async () => {
      setOpen(false);
      setTitle('');
      setPitch('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['ideas'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const rows = listQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Ideas & Initiatives
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Draft → Proposed → Under Review → Approved (converts to an initiative project with
            budget). Ideas never carry money.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>New idea</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New idea</DialogTitle>
              <DialogDescription>
                Three fields. Sponsor defaults to you.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Pitch</Label>
                <Textarea
                  rows={5}
                  value={pitch}
                  onChange={(e) => setPitch(e.currentTarget.value)}
                  placeholder="What's the idea, who benefits, and what's the smallest first step?"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Category</Label>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.currentTarget.value as IdeaCategory)}
                  >
                    <option value="revenue">Revenue</option>
                    <option value="partnership">Partnership</option>
                    <option value="cost_saving">Cost saving</option>
                    <option value="process">Process</option>
                    <option value="product">Product</option>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Visibility</Label>
                  <Select
                    value={visibility}
                    onChange={(e) => setVisibility(e.currentTarget.value as IdeaVisibility)}
                  >
                    <option value="c_suite_and_sponsor">C-suite + sponsor</option>
                    <option value="all_staff">All staff</option>
                  </Select>
                </div>
              </div>
              {error ? (
                <Alert variant="destructive">
                  <AlertTitle>Could not create</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                onClick={() => createMutation.mutate()}
                disabled={title.trim() === '' || pitch.trim() === '' || createMutation.isPending}
              >
                {createMutation.isPending ? 'Saving…' : 'Save draft'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {(reviewsQuery.data ?? []).length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Initiatives due for review</CardTitle>
            <CardDescription>
              Continue, revise the budget, or kill. Each missed review also raises a queue item.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {(reviewsQuery.data ?? []).map((r) => (
                <li
                  key={r.projectId}
                  className="flex flex-col items-start gap-2 py-2 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                >
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: r.projectId }}
                    className="font-medium text-[hsl(var(--brand))] hover:underline"
                  >
                    {r.code} · {r.name}
                  </Link>
                  <Badge variant="destructive" className="shrink-0 whitespace-nowrap">
                    Review due {r.reviewDueOn}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-4">
          <div>
            <CardTitle>All ideas</CardTitle>
            <CardDescription>{rows.length} shown</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="s" className="text-xs">Filter</Label>
            <Select
              id="s"
              className="h-9 w-40"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.currentTarget.value as IdeaStatus | '')}
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="proposed">Proposed</option>
              <option value="under_review">Under review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="parked">Parked</option>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No ideas visible to you.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col gap-2 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium">
                      <Link to="/ideas/$id" params={{ id: r.id }}>
                        {r.title}
                      </Link>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {r.category} · sponsor {r.sponsorName ?? r.sponsorId} · {r.voteCount} vote(s) ·{' '}
                      {r.commentCount} comment(s)
                      {r.revisitOn ? ` · revisit ${r.revisitOn}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    {r.visibility === 'c_suite_and_sponsor' ? (
                      <Badge variant="muted">Confidential</Badge>
                    ) : null}
                    <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
